// Pure, framework-free CSV export builder for the RACI matrix (item 8 of the
// "import/export" bug report: export/import asymmetry).
//
// Extracted out of pages/index.vue's exportCSV() so the format can be unit-tested
// without booting Nuxt/Vue, and so the importer (app/Console/Commands/ImportMarketingCsv.php)
// and this module agree on the same contract:
//   - Header row 1 carries the category name once per role that HAS children, and
//     nothing at all for a role with zero children (previously a category with no
//     children still wrote one ';' cell in header 1 while header 2 wrote none for it,
//     shifting every column after it out of alignment).
//   - Header row 2 (and every data row) is keyed on section/role NAMES, matched by the
//     importer against the live project's sections/roles - never on column position or
//     count, so the file this module produces is safe to re-import unchanged even when
//     the number of sections or role categories differs from the historical 4/N.
//   - Delimiter is ';' (matches ImportMarketingCsv's default auto-detection and the
//     client's own files). Values are CSV-escaped (quotes doubled, the field wrapped in
//     quotes whenever it contains the delimiter, a quote, or a newline).
//   - A UTF-8 BOM is prepended so Excel and the importer's BOM-stripping both behave.

const DELIMITER = ';';
const BOM = '﻿';

/**
 * Escape a single CSV field for the given delimiter.
 */
export function escapeCsvField(value, delimiter = DELIMITER) {
    const str = value === null || value === undefined ? '' : String(value);
    if (str.includes(delimiter) || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
}

function joinRow(cells, delimiter = DELIMITER) {
    return cells.map((c) => escapeCsvField(c, delimiter)).join(delimiter);
}

/**
 * Resolve a translated display value the same way getDisplayName/getDisplaySectionName/
 * getDisplayTaskSectionValue do in pages/index.vue: prefer the current language, fall
 * back to NL, and pass strings through unchanged.
 */
export function displayValue(value, currentViewLanguage = 'NL') {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                return parsed[currentViewLanguage] || parsed['NL'] || '';
            }
        } catch (e) {
            return value;
        }
    }
    if (typeof value === 'object') {
        return value[currentViewLanguage] || value['NL'] || '';
    }
    return String(value);
}

/**
 * Build the full export CSV (including the UTF-8 BOM) for a project + version.
 *
 * @param {object} params
 * @param {Array}  params.sections     project.sections
 * @param {Array}  params.roles        project.roles (top-level roles, each with .children)
 * @param {Array}  params.tasks        version.tasks
 * @param {Array}  params.raciCodes    project.raci_codes
 * @param {string} [params.currentViewLanguage]
 * @param {string} [params.delimiter]
 * @returns {string}
 */
export function buildRaciExportCsv({ sections, roles, tasks, raciCodes, currentViewLanguage = 'NL', delimiter = DELIMITER }) {
    sections = sections || [];
    roles = roles || [];
    tasks = tasks || [];
    raciCodes = raciCodes || [];

    // Only roles that actually have at least one child produce columns, on both
    // header rows - this is what keeps header 1 and header 2 the same length.
    const rolesWithChildren = roles.filter((role) => role.children && role.children.length > 0);

    const header1 = sections.map(() => '');
    const header2 = sections.map((section) => displayValue(section.name, currentViewLanguage));

    rolesWithChildren.forEach((role) => {
        role.children.forEach((child, index) => {
            header1.push(index === 0 ? displayValue(role.name, currentViewLanguage) : '');
            header2.push(displayValue(child.name, currentViewLanguage));
        });
    });

    const dataRows = tasks.map((task) => {
        const row = sections.map((section) => {
            const taskSection = (task.sections || []).find((s) => s.id === section.id || s.section_id === section.id);
            const value = taskSection ? taskSection.pivot?.value : null;
            return displayValue(value, currentViewLanguage) ?? '';
        });

        rolesWithChildren.forEach((role) => {
            role.children.forEach((child) => {
                const roleValue = (task.roles || []).find((r) => r.id === child.id);
                if (roleValue) {
                    const raciCode = raciCodes.find((r) => r.id === roleValue.pivot.raci_code_id);
                    row.push(raciCode ? raciCode.code : '');
                } else {
                    row.push('');
                }
            });
        });

        return row;
    });

    const lines = [joinRow(header1, delimiter), joinRow(header2, delimiter), ...dataRows.map((r) => joinRow(r, delimiter))];

    return BOM + lines.join('\r\n') + '\r\n';
}
